import { useCallback, useMemo } from 'react';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { useTranslation } from '@blocs.i18n';
import { useRouter } from '@blocs.router';
import routeHelper from '../../routes/routeHelper';
import { useArtistContext } from '../core/ArtistContext';

export type UseHeadshotAnnualModalParams = {
  /** Closes the open headshots modal; owned by `useHeadshotModal`. */
  onClose: () => void;
};

/**
 * Annual "Thank you for booking headshots with us" modal shown once both yearly
 * sessions are used: the next booking date and the Upload Media
 * action. `useHeadshotModal` owns which modal is open.
 */
export const useHeadshotAnnualModal = ({ onClose }: UseHeadshotAnnualModalParams) => {
  const { headshotEligibility, myAccount } = useArtistContext();
  const { formatIntlDate } = useTranslation();
  const { navigate } = useRouter();
  const nextSessionDate = headshotEligibility?.creditsResetAt
    ? formatIntlDate({
        date: headshotEligibility.creditsResetAt,
        format: 'MONTH_LONG_DAY_YEAR',
        timeZoneStandardName: myAccount?.timeZone?.standardName,
        replaceUtc: false,
      })
    : '';

  const handleUploadMedia = useCallback(() => {
    captureHeapEvent({
      name: HEAP_EVENT_NAMES.TALENT_CLICK_HEADSHOTS_ANNUAL_UPGRADE_MODULE_UPLOAD_MEDIA,
    });
    onClose();
    navigate({ to: routeHelper.mediaBankType.link({ mediaType: 'photo' }) });
  }, [navigate, onClose]);

  return useMemo(() => ({ nextSessionDate, handleUploadMedia }), [handleUploadMedia, nextSessionDate]);
};

export type UseHeadshotAnnualModalValues = ReturnType<typeof useHeadshotAnnualModal>;
